import type { ContentSectionKey, SiteContent } from './schema'

/**
 * Client for the content API.
 *
 * The base URL is build-time configuration, not a secret — it is only an
 * address. Credentials never appear here: the Cloudinary keys live on the
 * server, and the admin's token is obtained by logging in.
 */
const BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'

export interface UploadResult {
  url: string
  publicId: string
  /** `image` or `raw` — documents are stored raw so they download intact. */
  resourceType?: string
  bytes?: number
  width: number
  height: number
  /**
   * Set when the file stored fine but cannot be served — Cloudinary blocks
   * PDF delivery by default, so a CV uploads and then 401s to visitors.
   */
  warning?: string
}

/**
 * The Cloudinary subfolders the admin may upload into.
 *
 * Mirrors `FOLDERS` in `api/routers/upload_router.py`, which is the
 * authority: anything not on that list is filed under `misc` rather than
 * rejected, so a new field cannot break an upload.
 */
export type MediaFolder =
  | 'portraits'
  | 'brand'
  | 'projects'
  | 'documents'
  | 'research'
  | 'people'
  | 'misc'

const json = async (response: Response) => {
  if (!response.ok) {
    const detail = await response
      .json()
      .then((body: { detail?: string }) => body.detail)
      .catch(() => undefined)
    throw new Error(detail ?? `Request failed (${response.status})`)
  }
  return response.json()
}

/** Overrides stored on the server. Returns `null` if it cannot be reached. */
export const fetchContent = async (): Promise<Partial<SiteContent> | null> => {
  try {
    const response = await fetch(`${BASE}/api/content`)
    if (!response.ok) return null
    return (await response.json()) as Partial<SiteContent>
  } catch {
    // Offline, CORS, or no API deployed — the caller falls back to defaults.
    return null
  }
}

export const login = async (
  email: string,
  password: string,
): Promise<{ token: string; email: string }> =>
  json(
    await fetch(`${BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    }),
  )

export const verifyToken = async (token: string): Promise<boolean> => {
  try {
    const response = await fetch(`${BASE}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    return response.ok
  } catch {
    return false
  }
}

/**
 * What a write reports back about the media library.
 *
 * `removed` lists the Cloudinary public ids the server deleted because
 * nothing on the site refers to them any more. Surfaced in the panel rather
 * than kept quiet: a save that silently destroys a file is the kind of thing
 * you only discover much later.
 */
export interface WriteResult {
  removed: string[]
}

/**
 * Writes one section, leaving the rest of the document alone.
 *
 * What the admin panel uses. A save can then only ever affect the thing
 * being edited — two tabs open on different sections cannot overwrite one
 * another, and a failure is contained to one part of the site.
 */
export const saveSection = async (
  token: string,
  section: ContentSectionKey,
  value: unknown,
): Promise<WriteResult> => {
  const body = (await json(
    await fetch(`${BASE}/api/content/${section}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ value }),
    }),
  )) as { removed?: string[] }
  return { removed: body.removed ?? [] }
}

/**
 * Drops the override for one section so it falls back to the value compiled
 * into the site — the undo for an edit that made things worse.
 */
export const resetSection = async (
  token: string,
  section: ContentSectionKey,
): Promise<WriteResult> => {
  const body = (await json(
    await fetch(`${BASE}/api/content/${section}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    }),
  )) as { removed?: string[] }
  return { removed: body.removed ?? [] }
}

/** Replaces the whole document. Kept for import and restore. */
export const saveContent = async (
  token: string,
  content: SiteContent,
): Promise<void> => {
  await json(
    await fetch(`${BASE}/api/content`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(content),
    }),
  )
}

export const uploadImage = async (
  token: string,
  file: File,
  folder: MediaFolder = 'misc',
): Promise<UploadResult> => {
  const form = new FormData()
  form.append('file', file)
  form.append('folder', folder)
  return json(
    await fetch(`${BASE}/api/upload/image`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    }),
  )
}
